import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Building2,
  User,
  Phone,
  Navigation,
  Camera,
  CheckCheck,
  ChevronRight,
  ArrowRight,
  ShieldAlert,
  ShieldCheck,
  Briefcase,
  FileText,
  Hammer,
  Zap,
  RotateCw,
  ExternalLink,
  Award,
  Layers,
  TrendingUp,
} from 'lucide-react';
import { StaffItem, WorkOrderItem, FieldAttendanceItem } from '../types/index.ts';
import { useAuth } from '../context/AuthContext.tsx';

interface MobileJobPageProps {
  onNavigate: (route: string, param?: string) => void;
  staffIdParam?: string;
}

export const MobileJobPage: React.FC<MobileJobPageProps> = ({ onNavigate, staffIdParam }) => {
  const { currentUser, currentRole } = useAuth();

  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState<'ALL' | 'ACTIVE' | 'PENDING' | 'COMPLETED'>('ALL');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // GPS Check-In / Check-Out Modal
  const [gpsModal, setGpsModal] = useState<{
    isOpen: boolean;
    job: any | null;
    mode: 'CHECK_IN' | 'CHECK_OUT';
  }>({
    isOpen: false,
    job: null,
    mode: 'CHECK_IN',
  });

  const [gpsLocating, setGpsLocating] = useState(false);
  const [gpsLatitude, setGpsLatitude] = useState<string>('24.8214');
  const [gpsLongitude, setGpsLongitude] = useState<string>('67.0321');
  const [gpsNotes, setGpsNotes] = useState('');
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [submittingGps, setSubmittingGps] = useState(false);

  // Evidence Photo Modal
  const [photoModal, setPhotoModal] = useState<{
    isOpen: boolean;
    job: any | null;
  }>({
    isOpen: false,
    job: null,
  });
  const [photoPhase, setPhotoPhase] = useState<'BEFORE' | 'DURING' | 'AFTER'>('DURING');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Field Progress Modal
  const [progressModal, setProgressModal] = useState<{
    isOpen: boolean;
    job: any | null;
  }>({
    isOpen: false,
    job: null,
  });
  const [mobileProgressStage, setMobileProgressStage] = useState('50%');
  const [mobileProgressPercent, setMobileProgressPercent] = useState(50);
  const [mobileProgressDesc, setMobileProgressDesc] = useState('');
  const [mobileMaterialUsage, setMobileMaterialUsage] = useState('');
  const [mobileRemainingMaterial, setMobileRemainingMaterial] = useState('');
  const [submittingProgress, setSubmittingProgress] = useState(false);

  // Job Completion Modal
  const [completeModal, setCompleteModal] = useState<{
    isOpen: boolean;
    job: any | null;
  }>({
    isOpen: false,
    job: null,
  });
  const [clientRepName, setClientRepName] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [clientSignoffRemarks, setClientSignoffRemarks] = useState('');
  const [actualMaterialUsage, setActualMaterialUsage] = useState('');
  const [remainingMaterial, setRemainingMaterial] = useState('');
  const [completionPhotoUrl, setCompletionPhotoUrl] = useState('');
  const [clientAcknowledged, setClientAcknowledged] = useState(true);
  const [submittingComplete, setSubmittingComplete] = useState(false);

  // Fetch all staff
  const isAdminOrSupervisor =
    currentRole === 'ADMIN' ||
    currentRole === 'MANAGEMENT' ||
    currentRole === 'PROJECT_MANAGER' ||
    currentRole === 'SUPERVISOR';

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await fetch('/api/staff');
        if (res.ok) {
          const list: StaffItem[] = await res.json();
          setStaffList(list);

          // Find authenticated staff by matching email or name
          const authenticatedStaff =
            list.find(
              (s) =>
                (s.email && currentUser?.email && s.email.toLowerCase() === currentUser.email.toLowerCase()) ||
                (s.name && currentUser?.name && s.name.toLowerCase() === currentUser.name.toLowerCase())
            ) || list[0];

          if (!isAdminOrSupervisor) {
            // PRODUCTION FIELD STAFF SECURITY: Strict binding to own staff identity
            if (authenticatedStaff) {
              setSelectedStaffId(authenticatedStaff.id);
            }
          } else {
            // Admin/Supervisor can use param or default
            if (staffIdParam) {
              setSelectedStaffId(Number(staffIdParam));
            } else if (authenticatedStaff) {
              setSelectedStaffId(authenticatedStaff.id);
            } else if (list.length > 0) {
              setSelectedStaffId(list[0].id);
            }
          }
        }
      } catch (err: any) {
        console.warn('Staff fetch notice:', err.message);
      }
    };
    fetchStaff();
  }, [staffIdParam, currentRole, currentUser?.email, isAdminOrSupervisor]);

  // Fetch jobs for selected staff with role headers
  const fetchJobs = async () => {
    if (!selectedStaffId) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/mobile/jobs?staffId=${selectedStaffId}`, {
        headers: {
          'x-user-role': currentRole,
          'x-staff-id': String(selectedStaffId),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      } else if (res.status === 403) {
        setToastMessage('Security Notice: You are only authorized to access your own assigned jobs.');
        setJobs([]);
      }
    } catch (err: any) {
      console.warn('Jobs fetch notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStaffId) {
      fetchJobs();
    }
  }, [selectedStaffId]);

  const activeStaff = staffList.find((s) => s.id === selectedStaffId);

  // Geolocation trigger
  const handleCaptureLocation = () => {
    if (!navigator.geolocation) {
      setToastMessage('Geolocation is not supported by your browser. Using test coordinates.');
      return;
    }
    setGpsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLatitude(pos.coords.latitude.toFixed(6));
        setGpsLongitude(pos.coords.longitude.toFixed(6));
        setGpsLocating(false);
        setToastMessage(`GPS coordinates captured accurately (${pos.coords.accuracy.toFixed(1)}m precision)`);
      },
      (err) => {
        setGpsLocating(false);
        setToastMessage(`GPS capture notice: ${err.message}. Using calibrated fallback coordinates.`);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Submit GPS Check-In / Check-Out
  const handleGpsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gpsModal.job || !selectedStaffId) return;

    try {
      setSubmittingGps(true);
      const isCheckIn = gpsModal.mode === 'CHECK_IN';
      const endpoint = isCheckIn ? '/api/gps/checkin' : '/api/gps/checkout';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: gpsModal.job.ticketId,
          workOrderId: gpsModal.job.id,
          staffId: selectedStaffId,
          staffName: activeStaff?.name,
          eventType: gpsModal.mode,
          latitude: gpsLatitude,
          longitude: gpsLongitude,
          branchCode: gpsModal.job.branchCode,
          notes: gpsNotes,
          isManualOverride,
          overrideReason,
          supervisorName: activeStaff?.name,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        if (data.isGpsVerified) {
          setToastMessage(
            `GPS ${isCheckIn ? 'Check-In' : 'Check-Out'} VERIFIED! Distance: ${
              data.distanceMeters ? Number(data.distanceMeters).toFixed(1) + 'm' : 'Site Benchmarked'
            }`
          );
        } else {
          setToastMessage(`Notice: GPS recorded outside branch radius (${Number(data.distanceMeters).toFixed(1)}m)`);
        }
        setGpsModal({ isOpen: false, job: null, mode: 'CHECK_IN' });
        await fetchJobs();
      } else {
        setToastMessage(data.error || 'GPS submission failed');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'GPS network error');
    } finally {
      setSubmittingGps(false);
      setTimeout(() => setToastMessage(null), 6000);
    }
  };

  // Submit Evidence Photo
  const handlePhotoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!photoModal.job) return;

    try {
      setUploadingPhoto(true);
      const urlToUse =
        photoUrl.trim() ||
        'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=800&auto=format&fit=crop&q=80';

      const res = await fetch('/api/evidence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: photoModal.job.ticketId,
          workOrderId: photoModal.job.id,
          phase: photoPhase,
          caption: photoCaption || `Field execution evidence - ${photoPhase}`,
          photoUrl: urlToUse,
          latitude: photoModal.job.latitude || '24.8607',
          longitude: photoModal.job.longitude || '67.0011',
          isGpsVerified: true,
        }),
      });

      if (res.ok) {
        setToastMessage(`Evidence photo uploaded (${photoPhase} Phase)`);
        setPhotoModal({ isOpen: false, job: null });
        setPhotoCaption('');
        setPhotoUrl('');
        await fetchJobs();
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Evidence upload error');
    } finally {
      setUploadingPhoto(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Start Job Execution (captures GPS event WORK_START, updates status to IN_PROGRESS)
  const handleStartWork = async (jobId: number) => {
    try {
      const job = jobs.find((j) => j.id === jobId);
      const res = await fetch(`/api/work-orders/${jobId}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': currentRole,
          'x-staff-id': String(selectedStaffId || ''),
        },
        body: JSON.stringify({
          staffId: selectedStaffId,
          staffName: activeStaff?.name,
          ticketId: job?.ticketId,
          latitude: gpsLatitude || job?.latitude || '24.8214',
          longitude: gpsLongitude || job?.longitude || '67.0321',
          branchCode: job?.branchCode || 'UBL-0142',
          notes: `Work execution started on site by ${activeStaff?.name || 'Field Technician'}.`,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setToastMessage('Work started! GPS WORK_START event recorded. Status updated to IN_PROGRESS.');
        await fetchJobs();
      } else {
        setToastMessage(data.error || 'Failed to start job');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Failed to start job');
    }
  };

  // Field Progress Submission (25%, 50%, 75%, Completed with photos & material usage)
  const handleProgressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!progressModal.job) return;

    try {
      setSubmittingProgress(true);
      const res = await fetch(`/api/work-orders/${progressModal.job.id}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: progressModal.job.ticketId,
          progressStage: mobileProgressStage,
          progressPercent: mobileProgressPercent,
          description: mobileProgressDesc || `Milestone ${mobileProgressStage} reached on site.`,
          materialUsage: mobileMaterialUsage,
          remainingMaterial: mobileRemainingMaterial,
          photos: photoUrl ? [photoUrl] : [],
          latitude: gpsLatitude,
          longitude: gpsLongitude,
          staffId: selectedStaffId,
          staffName: activeStaff?.name || 'Field Technician',
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Progress updated: ${mobileProgressStage} (${mobileProgressPercent}%) recorded with GPS!`);
        setProgressModal({ isOpen: false, job: null });
        setMobileProgressDesc('');
        setMobileMaterialUsage('');
        setMobileRemainingMaterial('');
        await fetchJobs();
      } else {
        setToastMessage(data.error || 'Failed to update progress');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Progress update error');
    } finally {
      setSubmittingProgress(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Complete Job Execution
  const handleCompleteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completeModal.job) return;

    try {
      setSubmittingComplete(true);
      const res = await fetch(`/api/work-orders/${completeModal.job.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completionNotes,
          workSummary: completionNotes,
          actualMaterialUsage,
          remainingMaterial,
          clientRepresentativeName: clientRepName || 'Branch In-Charge',
          clientSignoffNotes: clientSignoffRemarks || 'All scope items verified on site.',
          photos: completionPhotoUrl ? [completionPhotoUrl] : [],
          latitude: gpsLatitude,
          longitude: gpsLongitude,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Job completed! Status transitioned to VERIFICATION_PENDING. Awaiting supervisor joint sign-off.`);
        setCompleteModal({ isOpen: false, job: null });
        setClientRepName('');
        setCompletionNotes('');
        setClientSignoffRemarks('');
        setActualMaterialUsage('');
        setRemainingMaterial('');
        setCompletionPhotoUrl('');
        await fetchJobs();
      } else {
        setToastMessage(data.error || 'Failed to complete job');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Job completion error');
    } finally {
      setSubmittingComplete(false);
      setTimeout(() => setToastMessage(null), 6000);
    }
  };

  const filteredJobs = jobs.filter((j) => {
    if (filterTab === 'ACTIVE') return j.status === 'IN_PROGRESS' || j.status === 'ON_SITE';
    if (filterTab === 'PENDING') return j.status === 'ASSIGNED';
    if (filterTab === 'COMPLETED') return j.status === 'COMPLETED' || j.status === 'VERIFIED';
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-12">
      {/* Top Mobile App Header */}
      <div className="bg-slate-900 text-white rounded-2xl p-4 sm:p-5 shadow-lg border border-slate-800 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center font-black shadow-md shrink-0">
              <Smartphone className="w-5 h-5 text-slate-950" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-black tracking-tight text-white">
                  HERE4U Field Employee Portal
                </h1>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-400 text-slate-950">
                  Mobile Job App
                </span>
              </div>
              <p className="text-xs text-slate-400">
                On-site execution workspace for UBL branch work orders &amp; GPS attendance.
              </p>
            </div>
          </div>

          {/* Quick refresh */}
          <button
            onClick={fetchJobs}
            className="self-start sm:self-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center gap-1.5 transition-colors"
          >
            <RotateCw className="w-3.5 h-3.5 text-amber-400" />
            Refresh Jobs
          </button>
        </div>

        {/* ACTIVE TECHNICIAN PROFILE SWITCHER */}
        <div className="pt-3 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-950/60 p-3 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border border-blue-400">
              {activeStaff?.profilePhoto ? (
                <img src={activeStaff.profilePhoto} alt={activeStaff.name} className="w-full h-full object-cover" />
              ) : (
                activeStaff?.name?.charAt(0) || 'T'
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Acting Technician:</span>
                <span className="font-bold text-sm text-amber-300">{activeStaff?.name || 'Technician'}</span>
                <span className="font-mono text-[10px] text-slate-400">({activeStaff?.employeeId})</span>
              </div>
              <div className="text-[11px] text-slate-300 flex items-center gap-2 mt-0.5">
                <span>{activeStaff?.designation}</span>
                <span>&bull;</span>
                <span className="text-blue-300">{activeStaff?.teamName}</span>
                <span>&bull;</span>
                <span
                  className={`px-1.5 py-0.2 rounded font-bold text-[10px] ${
                    activeStaff?.availability === 'ON_SITE'
                      ? 'bg-emerald-900/80 text-emerald-300'
                      : 'bg-blue-900/80 text-blue-300'
                  }`}
                >
                  {activeStaff?.availability || 'AVAILABLE'}
                </span>
              </div>
            </div>
          </div>

          {/* Profile Identity Security */}
          {isAdminOrSupervisor ? (
            /* Supervisor / Admin Emulation Mode */
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-amber-950/40 border border-amber-600/40 px-3 py-1.5 rounded-xl">
              <span className="text-[10px] font-bold text-amber-400 flex items-center gap-1 font-mono uppercase">
                <AlertTriangle className="w-3 h-3 text-amber-400" />
                Dev/Supervisor Preview:
              </span>
              <select
                value={selectedStaffId || ''}
                onChange={(e) => setSelectedStaffId(Number(e.target.value))}
                className="px-2.5 py-1 text-xs bg-slate-900 text-amber-200 border border-amber-600/60 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 font-semibold"
              >
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.skill} - {s.employeeId})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            /* Production Field Staff Identity Protected */
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-950/70 border border-emerald-500/40 rounded-xl text-emerald-300 text-xs">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="leading-tight">
                <span className="font-bold">Identity Verified</span>
                <span className="text-[10px] text-emerald-400 block font-mono">Bound to {activeStaff?.employeeId}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {toastMessage && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs font-semibold text-amber-950 flex items-center gap-2 shadow-xs animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        {(['ALL', 'ACTIVE', 'PENDING', 'COMPLETED'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setFilterTab(tab)}
            className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap ${
              filterTab === tab
                ? 'bg-slate-900 text-white shadow-xs'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab === 'ALL'
              ? `All Assigned Jobs (${jobs.length})`
              : tab === 'ACTIVE'
              ? 'On-Site / In Progress'
              : tab === 'PENDING'
              ? 'Pending Check-In'
              : 'Completed'}
          </button>
        ))}
      </div>

      {/* JOB CARDS LIST */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-xs bg-white rounded-2xl border border-slate-200">
          Loading assigned work orders...
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 space-y-2">
          <Briefcase className="w-8 h-8 text-slate-300 mx-auto" />
          <h3 className="font-bold text-slate-800 text-sm">No Jobs Assigned in this Category</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {activeStaff?.name} currently has no work orders assigned under {filterTab}.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => {
            const isOnSite = job.status === 'ON_SITE' || job.status === 'IN_PROGRESS';
            const isCompleted = job.status === 'COMPLETED' || job.status === 'VERIFIED';
            const hasCheckIn = Boolean(job.latestCheckIn);

            return (
              <div
                key={job.id}
                className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4 transition-all hover:border-slate-300"
              >
                {/* Header: Ticket, Work Order, Priority */}
                <div className="flex flex-wrap items-start justify-between gap-2 pb-3 border-b border-slate-100">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-black text-sm text-slate-950 font-mono tracking-tight">
                        {job.workOrderNumber}
                      </span>
                      <span className="text-slate-400">&bull;</span>
                      <span className="font-mono text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        {job.ticketNumber}
                      </span>
                      {job.workflowType === 'MINOR_URGENT' && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-black bg-rose-600 text-white uppercase tracking-wider">
                          FAST-TRACK MINOR URGENT
                        </span>
                      )}
                    </div>
                    <h2 className="font-bold text-slate-900 text-sm mt-1">{job.scope}</h2>
                  </div>

                  {/* Status Badge */}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-black tracking-wide ${
                      isCompleted
                        ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                        : isOnSite
                        ? 'bg-amber-100 text-amber-950 border border-amber-300 animate-pulse'
                        : 'bg-blue-100 text-blue-900 border border-blue-300'
                    }`}
                  >
                    {job.status}
                  </span>
                </div>

                {/* Branch Location & 1-Tap Navigation */}
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-900">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span>
                          {job.branchName} ({job.branchCode})
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{job.branchAddress || 'UBL Bank Branch'}</div>
                    </div>

                    {/* Navigation & Call Links */}
                    <div className="flex items-center gap-2">
                      {job.latitude && job.longitude && (
                        <a
                          href={`https://maps.google.com/?q=${job.latitude},${job.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2.5 py-1.5 text-xs font-bold rounded-lg bg-blue-700 text-white hover:bg-blue-800 flex items-center gap-1 shadow-xs"
                        >
                          <Navigation className="w-3.5 h-3.5 text-blue-200" />
                          Directions
                        </a>
                      )}
                      {job.branchPhone && (
                        <a
                          href={`tel:${job.branchPhone}`}
                          className="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                        >
                          <Phone className="w-3.5 h-3.5 text-emerald-600" />
                          Call Branch
                        </a>
                      )}
                    </div>
                  </div>

                  {/* GPS Verification Status */}
                  <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-slate-500">Benchmark Radius:</span>
                      <span className="font-semibold text-slate-700">{job.allowedRadiusMeters || 150}m</span>
                    </div>

                    {job.latestCheckIn ? (
                      <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        Checked-In ({job.latestCheckIn.notes?.slice(0, 32)}...)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-400 font-medium">
                        Not checked in yet
                      </span>
                    )}
                  </div>
                </div>

                {/* Scope & Instructions */}
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="font-bold text-slate-700">Service Category: </span>
                    <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                      {job.workType}
                    </span>
                  </div>

                  {job.instructions && (
                    <div className="p-2.5 bg-amber-50/60 border border-amber-200 rounded-lg text-amber-950 text-xs">
                      <span className="font-bold">Instructions: </span>
                      {job.instructions}
                    </div>
                  )}

                  {job.requiredMaterials && (
                    <div className="text-[11px] text-slate-600">
                      <span className="font-semibold text-slate-700">Materials: </span>
                      {job.requiredMaterials}
                    </div>
                  )}

                  {job.requiredTools && (
                    <div className="text-[11px] text-slate-600">
                      <span className="font-semibold text-slate-700">Tools: </span>
                      {job.requiredTools}
                    </div>
                  )}
                </div>

                {/* Evidence Photos Count */}
                {job.photosCount > 0 && (
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5 text-blue-600" />
                      {job.photosCount} Evidence Photos Uploaded
                    </span>
                    <span className="text-[11px] text-slate-500">Geotagged &amp; Verified</span>
                  </div>
                )}

                {/* ACTION BAR (TOUCH-FRIENDLY BUTTONS) */}
                <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Action 1: GPS Check-In */}
                    {!isCompleted && job.status !== 'IN_PROGRESS' && (
                      <button
                        onClick={() => {
                          setGpsModal({ isOpen: true, job, mode: 'CHECK_IN' });
                          setGpsNotes('Arrival at UBL Branch. Ready to execute assigned scope.');
                        }}
                        className={`px-3 py-2 text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-xs transition-colors ${
                          job.status === 'ON_SITE' || hasCheckIn
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        {job.status === 'ON_SITE' || hasCheckIn ? 'GPS Verified On-Site' : '1-Tap GPS Check-In'}
                      </button>
                    )}

                    {/* Action 2: Start Work (Separate from Check-In: changes ON_SITE -> IN_PROGRESS and records WORK_START) */}
                    {!isCompleted &&
                      job.status !== 'IN_PROGRESS' &&
                      (job.status === 'ON_SITE' ||
                        job.status === 'READY_TO_START' ||
                        job.status === 'SURVEYING' ||
                        job.status === 'MATERIAL_PENDING' ||
                        job.workflowType === 'MINOR_URGENT' ||
                        hasCheckIn) && (
                        <button
                          onClick={() => handleStartWork(job.id)}
                          className="px-3.5 py-2 text-xs font-bold rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 flex items-center gap-1.5 shadow-sm transition-colors"
                        >
                          <Hammer className="w-3.5 h-3.5 text-slate-950" />
                          Start Work
                        </button>
                      )}

                    {/* Action 3: Add Evidence Photo */}
                    <button
                      onClick={() => setPhotoModal({ isOpen: true, job })}
                      className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 flex items-center gap-1.5 transition-colors"
                    >
                      <Camera className="w-3.5 h-3.5 text-slate-600" />
                      Add Site Photo
                    </button>

                    {/* Action: Update Field Progress */}
                    {!isCompleted && (
                      <button
                        onClick={() => {
                          setProgressModal({ isOpen: true, job });
                          setMobileProgressDesc('');
                          setMobileMaterialUsage('');
                          setMobileRemainingMaterial('');
                        }}
                        className="px-3 py-2 text-xs font-semibold rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border border-indigo-200 flex items-center gap-1.5 transition-colors"
                      >
                        <TrendingUp className="w-3.5 h-3.5 text-indigo-600" />
                        Update Progress
                      </button>
                    )}

                    {/* Action 4: GPS Check-Out */}
                    {isOnSite && !isCompleted && (
                      <button
                        onClick={() => {
                          setGpsModal({ isOpen: true, job, mode: 'CHECK_OUT' });
                          setGpsNotes('Work execution completed on site. Departing branch.');
                        }}
                        className="px-3 py-2 text-xs font-bold rounded-xl bg-slate-800 hover:bg-slate-900 text-white flex items-center gap-1.5 shadow-xs transition-colors"
                      >
                        <Clock className="w-3.5 h-3.5 text-amber-400" />
                        GPS Check-Out
                      </button>
                    )}
                  </div>

                  {/* Action 5: Complete & Sign-off */}
                  {!isCompleted && (
                    <button
                      onClick={() => setCompleteModal({ isOpen: true, job })}
                      className="px-4 py-2 text-xs font-bold rounded-xl bg-blue-700 hover:bg-blue-800 text-white flex items-center gap-1.5 shadow-md ml-auto"
                    >
                      <CheckCheck className="w-4 h-4 text-blue-200" />
                      Complete &amp; Client Sign-off
                    </button>
                  )}

                  {isCompleted && (
                    <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200 ml-auto">
                      <Award className="w-4 h-4 text-emerald-600" />
                      Job Completed
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL 1: GPS CHECK-IN / CHECK-OUT */}
      {gpsModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-700" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    {gpsModal.mode === 'CHECK_IN' ? 'GPS Check-In at Branch' : 'GPS Check-Out & Departure'}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    {gpsModal.job?.branchName} ({gpsModal.job?.branchCode})
                  </p>
                </div>
              </div>
              <button
                onClick={() => setGpsModal({ isOpen: false, job: null, mode: 'CHECK_IN' })}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleGpsSubmit} className="p-5 space-y-4 text-xs">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-1">
                <div className="font-bold text-blue-950">Branch Geolocation Verification</div>
                <div className="text-[11px] text-blue-800">
                  Target coordinates: {gpsModal.job?.latitude || '24.8214'}, {gpsModal.job?.longitude || '67.0321'}
                  <br />
                  Allowed Radius: <span className="font-bold">{gpsModal.job?.allowedRadiusMeters || 150} meters</span>
                </div>
              </div>

              {/* Coordinates Inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Latitude</label>
                  <input
                    type="text"
                    required
                    value={gpsLatitude}
                    onChange={(e) => setGpsLatitude(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Longitude</label>
                  <input
                    type="text"
                    required
                    value={gpsLongitude}
                    onChange={(e) => setGpsLongitude(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Geolocation Button */}
              <button
                type="button"
                onClick={handleCaptureLocation}
                disabled={gpsLocating}
                className="w-full py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold rounded-xl border border-slate-300 flex items-center justify-center gap-1.5 transition-colors"
              >
                <Navigation className={`w-3.5 h-3.5 text-blue-600 ${gpsLocating ? 'animate-spin' : ''}`} />
                {gpsLocating ? 'Capturing High-Precision Device GPS...' : 'Capture Real-Time Phone GPS'}
              </button>

              {/* Attendance Notes */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Attendance Notes</label>
                <textarea
                  rows={2}
                  value={gpsNotes}
                  onChange={(e) => setGpsNotes(e.target.value)}
                  placeholder="e.g. Arrived on site with safety gear and tools ready."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Manual Override Checkbox */}
              <div className="pt-2 border-t border-slate-200 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="checkbox"
                    checked={isManualOverride}
                    onChange={(e) => setIsManualOverride(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-semibold text-xs">Supervisor Manual Override (if outside radius)</span>
                </label>

                {isManualOverride && (
                  <div className="space-y-1">
                    <label className="block text-rose-700 font-semibold text-[11px]">Override Reason *</label>
                    <input
                      type="text"
                      required
                      placeholder="Documented reason (minimum 5 chars)..."
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      className="w-full px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-lg text-xs focus:ring-1 focus:ring-rose-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setGpsModal({ isOpen: false, job: null, mode: 'CHECK_IN' })}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingGps}
                  className="px-5 py-2 text-xs font-bold rounded-xl bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50"
                >
                  {submittingGps ? 'Verifying...' : `Confirm ${gpsModal.mode}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: EVIDENCE PHOTO */}
      {photoModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">Add Site Photo Evidence</h3>
              </div>
              <button
                onClick={() => setPhotoModal({ isOpen: false, job: null })}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handlePhotoSubmit} className="p-5 space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Execution Phase *</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['BEFORE', 'DURING', 'AFTER'] as const).map((phase) => (
                    <button
                      key={phase}
                      type="button"
                      onClick={() => setPhotoPhase(phase)}
                      className={`py-2 text-xs font-bold rounded-lg border transition-colors ${
                        photoPhase === phase
                          ? 'bg-blue-700 text-white border-blue-800'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {phase}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Photo Caption / Detail</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Wall surface after applying first chemical waterproofing coat"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Photo Image URL</label>
                <input
                  type="text"
                  placeholder="Leave blank for test verified site photo snapshot"
                  value={photoUrl}
                  onChange={(e) => setPhotoUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  In live hardware, phone camera captures directly with automated GPS metadata.
                </p>
              </div>

              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setPhotoModal({ isOpen: false, job: null })}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingPhoto}
                  className="px-5 py-2 text-xs font-bold rounded-xl bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50"
                >
                  {uploadingPhoto ? 'Saving...' : 'Upload Evidence'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2.5: FIELD PROGRESS UPDATE (DRAWER/MODAL) */}
      {progressModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Update Field Progress</h3>
                  <p className="text-[11px] text-slate-500">{progressModal.job?.workOrderNumber}</p>
                </div>
              </div>
              <button
                onClick={() => setProgressModal({ isOpen: false, job: null })}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleProgressSubmit} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Milestone Stage *</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    value={mobileProgressStage}
                    onChange={(e) => {
                      const stg = e.target.value;
                      let pct = 50;
                      if (stg === '25%') pct = 25;
                      else if (stg === '50%') pct = 50;
                      else if (stg === '75%') pct = 75;
                      else if (stg === 'COMPLETED') pct = 100;
                      else if (stg === 'WORK_STARTED') pct = 10;
                      setMobileProgressStage(stg);
                      setMobileProgressPercent(pct);
                    }}
                  >
                    <option value="WORK_STARTED">Work Started (10%)</option>
                    <option value="25%">25% Complete</option>
                    <option value="50%">50% Complete</option>
                    <option value="75%">75% Complete</option>
                    <option value="COMPLETED">100% Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Progress %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={mobileProgressPercent}
                    onChange={(e) => setMobileProgressPercent(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Execution Description *</label>
                <textarea
                  rows={2}
                  required
                  placeholder="Describe work completed at this milestone..."
                  value={mobileProgressDesc}
                  onChange={(e) => setMobileProgressDesc(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Materials Used (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. 2 sheets ply installed"
                    value={mobileMaterialUsage}
                    onChange={(e) => setMobileMaterialUsage(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Remaining Materials</label>
                  <input
                    type="text"
                    placeholder="e.g. 2 sheets on site"
                    value={mobileRemainingMaterial}
                    onChange={(e) => setMobileRemainingMaterial(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between text-[11px]">
                <span className="font-semibold text-slate-700 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-blue-600" />
                  GPS Coordinates:
                </span>
                <span className="font-mono text-slate-600">{gpsLatitude}, {gpsLongitude}</span>
              </div>

              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setProgressModal({ isOpen: false, job: null })}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingProgress}
                  className="px-5 py-2 text-xs font-bold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                >
                  {submittingProgress ? 'Saving...' : 'Save Milestone Progress'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: JOB COMPLETION & SIGN-OFF */}
      {completeModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-blue-700" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Job Completion &amp; Client Sign-off</h3>
                  <p className="text-[11px] text-slate-500">{completeModal.job?.workOrderNumber}</p>
                </div>
              </div>
              <button
                onClick={() => setCompleteModal({ isOpen: false, job: null })}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCompleteSubmit} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">
                  Client Branch Representative Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sohail Abbasi (Branch Manager / Operations)"
                  value={clientRepName}
                  onChange={(e) => setClientRepName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Technician Work Completion Summary *</label>
                <textarea
                  rows={2}
                  required
                  placeholder="Summary of completed punch list, testing conducted, and cleanup..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Actual Materials Used</label>
                  <input
                    type="text"
                    placeholder="e.g. 4 sheets 16mm ply, 1 gal lacquer"
                    value={actualMaterialUsage}
                    onChange={(e) => setActualMaterialUsage(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Remaining Materials on Site</label>
                  <input
                    type="text"
                    placeholder="e.g. 1 sheet returned / 0 remaining"
                    value={remainingMaterial}
                    onChange={(e) => setRemainingMaterial(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Final Completion Photo Evidence URL</label>
                <input
                  type="url"
                  placeholder="https://images.unsplash.com/... (site completion photo)"
                  value={completionPhotoUrl}
                  onChange={(e) => setCompletionPhotoUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Client Acknowledgment &amp; Remarks</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Satisfactorily executed after banking hours. No leakage observed."
                  value={clientSignoffRemarks}
                  onChange={(e) => setClientSignoffRemarks(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="clientAckCheck"
                  checked={clientAcknowledged}
                  onChange={(e) => setClientAcknowledged(e.target.checked)}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="clientAckCheck" className="text-slate-800 font-semibold cursor-pointer select-none">
                  Client representative physically inspected site and acknowledged completion
                </label>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-950 text-[11px] space-y-1">
                <span className="font-bold">Next Steps in HERE4U Workflow:</span>
                <p>
                  Upon submission, the Work Order and Ticket progress to COMPLETED / VERIFICATION_PENDING.
                  The supervising engineer will verify site evidence before generating the final Completion Certificate.
                </p>
              </div>

              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setCompleteModal({ isOpen: false, job: null })}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingComplete}
                  className="px-5 py-2 text-xs font-bold rounded-xl bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50"
                >
                  {submittingComplete ? 'Completing...' : 'Submit Final Completion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
